import { useState, type ComponentType, type ReactNode, type SVGProps } from 'react';
import { Link } from 'react-router-dom';
import { HOME } from '@shared/home';
import {
  RESORT_ACTIVITIES,
  RESORT_ACTIVITIES_MORE,
  RESORT_BEACH,
  RESORT_BEACH_MORE,
  RESORT_DINING,
  RESORT_DINING_MORE,
  RESORT_DINING_SOURCE,
  RESORT_HEALTH,
  RESORT_HEALTH_MORE,
  RESORT_OVERVIEW,
  RESORT_OVERVIEW_MORE,
  RESORT_POOLS,
  RESORT_POOLS_MORE,
  RESORT_PRACTICAL,
  RESORT_PRACTICAL_MORE,
  RESORT_SOURCES,
  type ResortLine,
} from '@shared/resort';
import { usePlaces } from '@/hooks/usePlaces';
import { useFavorites } from '@/hooks/useFavorites';
import { PlaceCard } from '@/components/PlaceCard';
import { MapActions } from '@/components/MapActions';
import { ChevronRightIcon, HomeIcon, LighthouseIcon, PlacesIcon } from '@/components/Icons';
import logoMark from '@/assets/rocha-brava-mark.svg';
import { Bilingual } from '@/components/Bilingual';

type SectionIcon = ComponentType<SVGProps<SVGSVGElement>>;

interface ResortSection {
  id: string;
  title: string;
  accent: string;
  icon: SectionIcon;
}

const SECTIONS: ResortSection[] = [
  { id: 'home', title: 'Arrival', accent: 'home', icon: HomeIcon },
  { id: 'pools', title: 'Pools', accent: 'amenities', icon: LighthouseIcon },
  { id: 'beach', title: 'Beaches and walks', accent: 'nearby', icon: LighthouseIcon },
  { id: 'dining', title: 'Dining', accent: 'dining', icon: PlacesIcon },
  { id: 'practical', title: 'Wi-Fi and housekeeping', accent: 'home', icon: HomeIcon },
  { id: 'activities', title: 'Tennis, gym, and kids', accent: 'amenities', icon: LighthouseIcon },
  { id: 'health', title: 'Pharmacy and taxis', accent: 'nearby', icon: PlacesIcon },
  { id: 'nearby', title: 'Near the resort', accent: 'nearby', icon: PlacesIcon },
];

function LineList({ lines }: { lines: ResortLine[] }) {
  return (
    <ul className="rb-lines">
      {lines.map((line) => (
        <li key={line.title}>
          <strong>
            {line.href ? (
              <Link to={line.href}>{line.gloss ? <Bilingual text={line.title} /> : line.title}</Link>
            ) : line.gloss ? (
              <Bilingual text={line.title} />
            ) : (
              line.title
            )}
          </strong>
          <span>{line.body}</span>
        </li>
      ))}
    </ul>
  );
}

function ShowMore({ more }: { more: ResortLine[] }) {
  const [openMore, setOpenMore] = useState(false);
  return (
    <>
      {openMore ? <LineList lines={more} /> : null}
      <button
        type="button"
        className="rb-more"
        aria-expanded={openMore}
        onClick={() => setOpenMore((value) => !value)}
      >
        {openMore ? 'Show less' : 'Show more'}
      </button>
    </>
  );
}

function ResortCard({
  section,
  open,
  onToggle,
  children,
}: {
  section: ResortSection;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  const panelId = `${section.id}-panel`;
  const headingId = `${section.id}-heading`;
  const Icon = section.icon;
  return (
    <section
      id={section.id}
      className={`rb-section rb-accent rb-accent--${section.accent}`}
      aria-labelledby={headingId}
    >
      <h2 id={headingId} className="rb-section__heading">
        <button
          type="button"
          className="rb-section__toggle"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={onToggle}
        >
          <span className="rb-chip" aria-hidden="true">
            <Icon width={20} height={20} />
          </span>
          <span className="rb-section__title">{section.title}</span>
          <ChevronRightIcon className={open ? 'rb-chevron rb-chevron--open' : 'rb-chevron'} />
        </button>
      </h2>
      <div id={panelId} role="region" aria-labelledby={headingId} hidden={!open} className="rb-section__panel">
        {children}
      </div>
    </section>
  );
}

export function ResortPage() {
  const { places } = usePlaces();
  const { isFavorite, toggle } = useFavorites();
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const nearby = places.filter((p) => p.locality === 'inside' && p.id !== 'rocha-brava');

  function toggleSection(id: string) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function bodyFor(id: string): ReactNode {
    if (id === 'home') {
      return (
        <>
          {HOME.addressLines.map((line) => (
            <p key={line}>
              <Bilingual text={line} />
            </p>
          ))}
          <p>
            Reception is open 24 hours. Phone{' '}
            <a href={`tel:${HOME.phone.replace(/\s+/g, '')}`}>{HOME.phone}</a>. WhatsApp +351 969 924 581. Email{' '}
            <a href="mailto:rochabrava@rochabrava.com">rochabrava@rochabrava.com</a>.
          </p>
          <LineList lines={RESORT_OVERVIEW} />
          <ShowMore more={RESORT_OVERVIEW_MORE} />
          <MapActions
            address={HOME.address}
            lat={HOME.lat}
            lng={HOME.lng}
            phone={HOME.phone}
            website={HOME.website}
            name={HOME.name}
          />
          <div className="action-row">
            <a className="action-btn" href={`tel:${HOME.bookings.replace(/\s+/g, '')}`}>
              Bookings {HOME.bookings}
            </a>
          </div>
          <p className="muted" style={{ marginTop: 10 }}>
            Fax {HOME.fax}
          </p>
        </>
      );
    }
    if (id === 'pools') {
      return (
        <>
          <LineList lines={RESORT_POOLS} />
          <ShowMore more={RESORT_POOLS_MORE} />
        </>
      );
    }
    if (id === 'beach') {
      return (
        <>
          <LineList lines={RESORT_BEACH} />
          <ShowMore more={RESORT_BEACH_MORE} />
        </>
      );
    }
    if (id === 'dining') {
      return (
        <>
          <ul className="rb-lines">
            {RESORT_DINING.map((item) => (
              <li key={item.name}>
                <strong>
                  <Bilingual text={item.name} />
                </strong>
                <span>{item.detail}</span>
              </li>
            ))}
          </ul>
          <ShowMore more={RESORT_DINING_MORE} />
          <p className="event-source">
            Source:{' '}
            <a href={RESORT_DINING_SOURCE} target="_blank" rel="noreferrer">
              rochabrava.com
            </a>
          </p>
        </>
      );
    }
    if (id === 'practical') {
      return (
        <>
          <LineList lines={RESORT_PRACTICAL} />
          <ShowMore more={RESORT_PRACTICAL_MORE} />
        </>
      );
    }
    if (id === 'activities') {
      return (
        <>
          <LineList lines={RESORT_ACTIVITIES} />
          <ShowMore more={RESORT_ACTIVITIES_MORE} />
        </>
      );
    }
    if (id === 'health') {
      return (
        <>
          <LineList lines={RESORT_HEALTH} />
          <ShowMore more={RESORT_HEALTH_MORE} />
        </>
      );
    }
    return (
      <>
        {nearby.map((p) => (
          <PlaceCard key={p.id} place={p} isFavorite={isFavorite(p.id)} onToggleFavorite={toggle} />
        ))}
        <Link to="/places?locality=inside" className="list-row">
          All in-town places
        </Link>
      </>
    );
  }

  return (
    <div className="rb-page">
      <div className="page-header resort-letterhead">
        <img className="resort-letterhead__mark" src={logoMark} alt="" width={56} height={46} />
        <div>
          <h1 className="page-title">Rocha Brava</h1>
          <p className="page-subtitle">Village Resort — home pin</p>
        </div>
      </div>

      <div className="rb-sections">
        {SECTIONS.map((section) => (
          <ResortCard
            key={section.id}
            section={section}
            open={open.has(section.id)}
            onToggle={() => toggleSection(section.id)}
          >
            {bodyFor(section.id)}
          </ResortCard>
        ))}
      </div>

      <p className="event-source">
        Sources:{' '}
        {RESORT_SOURCES.map((source, index) => (
          <span key={source.href}>
            {index > 0 ? ' · ' : null}
            <a href={source.href} target="_blank" rel="noreferrer">
              {source.label}
            </a>
          </span>
        ))}
      </p>
    </div>
  );
}
